import React, {
	FC, useState, useCallback, useEffect, useMemo, Fragment,
} from 'react';
import { motion } from 'framer-motion';
import classNames from 'classnames';
import { isEqual, noop, uniqueId } from 'lodash';
import {
	MetricOption, MoveDown, SGChartType, SuperGridChart, useOpen, PlusIcon, useUrlParams, Button,
} from '@aleph/shared-components';
import {
	breakdownTypes,
	chartMetrics,
	getChartColor,
	getChartMetrics,
} from 'wb-spotify/app/routes/superGrid/actionRow/charts/utils';
import { SpotifyLevel } from 'wb-spotify/app/routes/superGrid/utils';
import useSpotifyCharts from 'wb-spotify/app/routes/superGrid/actionRow/charts/useSpotifyCharts';
import {
	DndContext,
	MouseSensor,
	useSensor,
	useSensors,
	useDroppable,
	DragOverlay,
	closestCorners,
} from '@dnd-kit/core';
import {
	SortableContext, sortableKeyboardCoordinates, horizontalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import {
	restrictToWindowEdges,
} from '@dnd-kit/modifiers';
import SortableChart from 'wb-spotify/app/routes/superGrid/actionRow/charts/sortableChart';
import { SmartPointerSensor } from 'wb-spotify/app/routes/superGrid/actionRow/charts/pointerSensor';
import { SmartKeyboardSensor } from 'wb-spotify/app/routes/superGrid/actionRow/charts/keyboardSensor';
import { BodyScrollEvent, GridApi } from 'ag-grid-community';
import { SPOTIFY_SUPERGRID_STATISTICS_DIMENSIONS } from 'wb-spotify/app/routes/superGrid/statistics/utils';
import { useNavigate, useParams } from 'react-router-dom';

const chartComponetsList: SGChartType[] = [
	{
		id:     uniqueId('chart-'),
		type:   'time',
		fields: [
			getChartMetrics('time').find(metric => metric.value === 'listeners'),
			{ ...chartMetrics.find(metric => metric.value === 'clicks'), type: 'bar' },
		] as MetricOption[],
	},
	{
		id:     uniqueId('chart-'),
		type:   'time',
		fields: [
			getChartMetrics('time').find(metric => metric.value === 'listeners'),
			getChartMetrics('time').find(metric => metric.value === 'spend'),
		] as MetricOption[],
	},
	{
		id:     uniqueId('chart-'),
		type:   'country',
		fields: [
			getChartMetrics('country').find(metric => metric.value === 'listeners'),
		] as MetricOption[],
	},
];

const CHARTS_CONTAINER_HEIGHT = 'calc(39vh - 65px)';

const SpotifyChartsContainer: FC<{ gridApi: GridApi | null }> = ({ gridApi }) => {
	const navigate = useNavigate();

	const { params, setUrlParam } = useUrlParams({ navigate, useParams });

	const [open, { onOpen, onClose }] = useOpen(params?.charts?.open ?? true);
	const [showCharts, setShowCharts] = useState(false);
	const [manualOpenOverridden, setManualOpenOverridden] = useState(!open);
	const [chartsHovering, setChartsHovering] = useState(false);

	const level: SpotifyLevel = params?.level ?? SpotifyLevel.AD_ACCOUNT;
	const superGridBody = useMemo(() => document.querySelector('#super-grid .ag-body-viewport'), [gridApi]);

	const onGridScroll = (e: BodyScrollEvent) => {
		const isScrollable = ((superGridBody?.scrollHeight ?? 0) > (superGridBody?.clientHeight ?? 0));

		if (!isScrollable || manualOpenOverridden) {
			return;
		}

		if (e.top > 0) {
			onClose();
		} else {
			onOpen();
		}
	};

	const handleTopScrollWithoutScrollbar = (e: Event) => {
		const isScrollable = ((superGridBody?.scrollHeight ?? 0) > (superGridBody?.clientHeight ?? 0));

		if (isScrollable || manualOpenOverridden) {
			return;
		}

		if ((e as WheelEvent).deltaY < 0) {
			onOpen();
		}
	};

	useEffect(() => {
		gridApi?.addEventListener('bodyScroll', onGridScroll);
		superGridBody?.addEventListener('wheel', handleTopScrollWithoutScrollbar, { passive: true });

		return () => {
			if (!gridApi?.isDestroyed()) {
				gridApi?.removeEventListener('bodyScroll', onGridScroll);
			}
			superGridBody?.removeEventListener('wheel', handleTopScrollWithoutScrollbar);
		};
	}, [gridApi, manualOpenOverridden, superGridBody]);

	const initialCharts = useMemo(() => params.charts?.state.map((chart: SGChartType) => {
		if (!Object.keys(chart).includes('id')) {
			return {
				...chart,
				id: uniqueId('chart-'),
			};
		}

		return chart;
	}), [params.charts?.state]);

	const [charts, setCharts] = useState<SGChartType[]>(initialCharts ?? chartComponetsList);
	const getColor = useCallback((idx?: number) => getChartColor({ idx, level }), [level]);
	const getPieColor = useCallback((idx?: number, count?: number) => getChartColor({
		idx,
		level,
		count,
	}, true), [level]);

	const [activeChart, setActiveChart] = useState<SGChartType | null>(null);

	const { setNodeRef } = useDroppable({
		id: 'droppable',
	});

	useEffect(() => {
		if (!isEqual(params.charts, { state: charts, open })) {
			setUrlParam('charts', { state: charts, open });
		}
	}, [charts, open]);

	const setChartMetrics = (idx) => (fields: MetricOption[]) => {
		setCharts(curr => {
			curr[idx] = {
				...curr[idx],
				fields,
			};

			return [...curr];
		});
	};

	const setChartType = (idx) => (type: string) => {
		setCharts(curr => {
			curr[idx] = {
				...curr[idx],
				type,
			};

			return [...curr];
		});
	};

	const removeChartComponent = (removeIdx) => () => {
		setCharts(curr => curr.filter((_, idx) => idx !== removeIdx));
	};

	const sensors = useSensors(
		useSensor(SmartPointerSensor, {
			activationConstraint: {
				distance: 10,
			},
		}),
		useSensor(SmartKeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
			keyboardCodes:    {
				start:  ['Space', 'Enter'],
				end:    ['Space', 'Enter'],
				cancel: ['Escape', 'Tab'],
			},
		}),
		useSensor(MouseSensor, {
			activationConstraint: {
				distance: 10,
			},
		}),
	);

	useEffect(() => {
		if (charts.some(chart => chart.isNew)) {
			setCharts(curr => curr.map(chart => ({
				...chart,
				isNew: false,
			})));
		}
	}, [charts]);

	const addChartComponent = () => {
		setCharts(
			curr => [...curr, {
				id:     uniqueId('chart-'),
				type:   'time',
				isNew:  true,
				fields: [],
			}],
		);
	};

	const handleDragMove = ({ active }) => {
		if (active?.id) {
			const actChart = charts.find(chart => chart.id === active.id) as SGChartType;
			setActiveChart(actChart);
		}
	};

	const handleDragEnd = ({ active, over }) => {
		setActiveChart(null);

		if (!over) {
			return;
		}

		if (active.id !== over.id) {
			const activeIndex = active.data.current.sortable.index;
			const overIndex = over.data.current?.sortable.index || 0;

			setCharts((items) => arrayMove(
				items,
				activeIndex,
				overIndex,
			));
		}
	};

	const onAnimationComplete = (e: { height: string }) => {
		if (e?.height === CHARTS_CONTAINER_HEIGHT) {
			setManualOpenOverridden(false);
			setShowCharts(true);
		} else {
			setShowCharts(false);
		}
	};

	const containerHeight = useMemo(() => (
		open
			? CHARTS_CONTAINER_HEIGHT
			: (chartsHovering ? 40 : 8)), [open, chartsHovering]);
	const onHoverStart = useCallback(() => setChartsHovering(true), []);
	const onHoverEnd = useCallback(() => setChartsHovering(false), []);

	return (
		<DndContext
			sensors={sensors}
			onDragMove={handleDragMove}
			onDragEnd={handleDragEnd}
			onDragStart={handleDragMove}
			autoScroll={false}
			modifiers={[restrictToWindowEdges]}
			collisionDetection={closestCorners}
		>
			<motion.div
				className={
					classNames(
						`group/chart-bar relative flex flex-row text-blue gap-[16px] w-full h-full bg-white
						items-center justify-center border-b border-gray-500
						cursor-pointer`,
						{
							'py-[8px]':          showCharts,
							'hover:border-blue': !showCharts,
						},
					)
				}
				onHoverStart={onHoverStart}
				onHoverEnd={onHoverEnd}
				animate={{ height: containerHeight }}
				transition={{ duration: 0.3 }}
				onAnimationComplete={onAnimationComplete}
				onClick={!open ? () => {
					onOpen();
					setManualOpenOverridden(true);
				} : noop}
				ref={setNodeRef}
				data-testid="charts-container"
			>
				<SortableContext
					disabled={!open}
					items={charts}
					strategy={horizontalListSortingStrategy}
					key={charts?.length}
				>

					{
						!open && <div className="group-hover/chart-bar:opacity-100 opacity-0 duration-200
							flex gap-[8px] items-center pointer-events-none"
						>
							<MoveDown width={16}/> <span className="text-[12px]">Show Charts</span>
						</div>
					}
					{
						open && showCharts && charts.map((chart, idx) => (
							<Fragment key={chart.id}>
								<div className="h-full flex-1"
									data-testid={`chart-${idx + 1}`}
								>
									<SortableChart
										id={chart.id}
										key={chart.id}
									>
										<SuperGridChart
											key={chart.id}
											removeChartComponent={removeChartComponent(idx)}
											chart={chart}
											level={SpotifyLevel.AD_ACCOUNT}
											breakdownChartMetrics={SPOTIFY_SUPERGRID_STATISTICS_DIMENSIONS}
											chartMetrics={getChartMetrics(chart.type)}
											chartsHook={useSpotifyCharts}
											getChartColor={getColor}
											getPieChartColor={getPieColor}
											setChartFields={setChartMetrics(idx)}
											setChartType={setChartType(idx)}
											breakdownTypes={breakdownTypes}
											isDragging={chart.id === activeChart?.id}
											editOpen={chart.isNew}
											navigate={navigate}
											useParams={useParams}
										/>
									</SortableChart>
								</div>

								{(idx < charts.length - 1) && <div className="border-r border-gray-300 h-[75%]" />}
							</Fragment>
						))
					}
					{ open && showCharts && charts.length < 4
						&& <div
							className="flex w-0 h-full py-[32px] justify-center items-center"
						>
							<div className="flex w-full h-full relative">
								<div className="absolute h-full w-[1px] bg-gray-300 left-[12px]" />
							</div>
							<div className='flex w-[24px] h-[32px] py-[4px] !bg-white z-10'>
								<Button
									onClick={addChartComponent}
									className='flex w-[24px] h-[24px] !p-0 justify-center items-center'
									leftNode={<PlusIcon />}
									size="small"
									appearance="ghost"
									color="gray"
									data-testid="add-chart-button"
								/>
							</div>
						</div>
					}
					{
						(chartsHovering && showCharts)
						&& <motion.div
							onClick={() => {
								setManualOpenOverridden(true);
								onClose();
							}}
							initial={{ rotate: 180 }}
							whileHover={{ y: -3 }}
							className={classNames('z-5 absolute bg-white items-center justify-center bottom-[-16px]', {
								'opacity-0': !open,
							})}
						>
							<Button
								leftNode={<MoveDown />}
								size="medium"
								appearance="ghost"
								color="gray"
								data-testid="collapse-charts-button"
							/>
						</motion.div>
					}
				</SortableContext>
			</motion.div>
			<motion.div className="h-[8px] absolute w-full z-[2]"
				onHoverStart={onHoverStart}
				onHoverEnd={onHoverEnd}
				onAnimationComplete={onAnimationComplete}
				data-testid="charts-resize-bar"
			/>
			{
				activeChart && (
					<DragOverlay
						adjustScale={false}
						className='z-1000 cursor-move bg-white'
						dropAnimation={{
							duration: 200,
							easing:   'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
						}}
						style={{
							boxShadow: 'rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
						}}
						modifiers={[restrictToWindowEdges]}
					>
						<SuperGridChart
							chart={activeChart}
							level={SpotifyLevel.AD_ACCOUNT}
							breakdownChartMetrics={SPOTIFY_SUPERGRID_STATISTICS_DIMENSIONS}
							chartMetrics={getChartMetrics(activeChart.type)}
							chartsHook={useSpotifyCharts}
							breakdownTypes={breakdownTypes}
							isDragging={true}
							navigate={navigate}
							useParams={useParams}
							getChartColor={getColor}
							getPieChartColor={getPieColor}
							/**
							 * We dont want the overlay chart to do any updates to the charts,
							 * for that reason we are passing noop functions.
							 */
							removeChartComponent={noop as any}
							setChartFields={noop as any}
							setChartType={noop as any}
						/>

					</DragOverlay>
				)
			}
		</DndContext>
	);
};

export default SpotifyChartsContainer;
